import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import IndustrySelect, { useIndustries } from "@/components/IndustrySelect";
import VisibilityToggle from "@/components/VisibilityToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useBulkSelect, SelectAllCheckbox, BulkBar } from "@/components/BulkBar";
import { Plus, Trash2, ChevronRight, Search, Pencil, PieChart } from "lucide-react";
import { ExportMenu } from "@/components/ExportMenu";
import CrmQuickAdd, { ContactCardFields, EMPTY_CONTACT, contactPayload } from "@/components/CrmQuickAdd";
import SortSelect, { sortRecords } from "@/components/SortSelect";
import ContributionPanel from "@/components/ContributionPanel";
import TrendPanel from "@/components/TrendPanel";
import { PaginationBar, paginate } from "@/components/Pagination";
import { useScrollRestore } from "@/hooks/useScrollRestore";
import { toast } from "sonner";

const EMPTY = { name: "", short_name: "", industry: "mining", visibility: "team", ...EMPTY_CONTACT };

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [industryFilter, setIndustryFilter] = useState("all");
  const [sort, setSort] = useState("az");
  const { industries } = useIndustries();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOwner = (user?.email || "").toLowerCase() === "ronick@somcoregroup.com";
  const sel = useBulkSelect();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkIndustry, setBulkIndustry] = useState("");
  const [bulkVisibility, setBulkVisibility] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [rev, setRev] = useState(null);
  const [page, setPage] = useState(1);
  const labelFor = (v) => industries.find((i) => i.value === v)?.label
    || (v ? v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");

  const load = () => api.get("/clients").then((r) => { setClients(r.data); setLoaded(true); });
  useEffect(() => { load(); api.get("/clients/analytics").then((r) => setRev(r.data)).catch(() => {}); }, []);
  useScrollRestore("clients", loaded);

  const create = async () => {
    try {
      const { name, industry, visibility } = form;
      const { data } = await api.post("/clients", { name, industry, visibility, short_name: form.short_name.trim(), ...contactPayload(form) });
      setOpen(false); toast.success("Client created");
      navigate(`/clients/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (e, id) => {
    if (!isOwner) { e.stopPropagation(); toast.error("Only the account owner can delete clients."); return; }
    e.stopPropagation();
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`); load(); toast.success("Client deleted");
  };

  const bulkDelete = async () => {
    const ids = [...sel.selected];
    if (!window.confirm(`Delete ${ids.length} client${ids.length !== 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/clients/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} client${data.deleted !== 1 ? "s" : ""}`);
      sel.clear(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkBusy(false); }
  };

  const bulkApply = async () => {
    const ids = [...sel.selected];
    const body = { ids };
    if (bulkIndustry) body.industry = bulkIndustry;
    if (bulkVisibility) body.visibility = bulkVisibility;
    if (!body.industry && !body.visibility) { toast.error("Choose an industry or visibility to apply."); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/clients/bulk-update", body);
      toast.success(`Updated ${data.updated} client${data.updated !== 1 ? "s" : ""}`);
      setBulkOpen(false); setBulkIndustry(""); setBulkVisibility(""); sel.clear(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkBusy(false); }
  };

  const filtered = clients.filter((c) => {
    if (industryFilter !== "all" && c.industry !== industryFilter) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    const primary = (c.contacts || []).find((x) => x.primary) || (c.contacts || [])[0] || {};
    return [c.name, primary.name, primary.email, c.contact_person, c.contact_email].some((v) => (v || "").toLowerCase().includes(s));
  });
  const usedIndustries = Array.from(new Set(clients.map((c) => c.industry).filter(Boolean)));
  const paged = paginate(sortRecords(filtered, sort), page);
  useEffect(() => { setPage(1); }, [q, industryFilter, sort]);

  return (
    <AppLayout title="Clients" actions={
      <div className="flex items-center gap-2">
        <ExportMenu kind="clients" selected={sel.selected} />
        <CrmQuickAdd kind="client" onManual={() => { setForm(EMPTY); setOpen(true); }} onCreated={load} />
      </div>
    }>
      {rev && rev.clients.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ContributionPanel
          title="Revenue by Client"
          subtitle="Where your won revenue concentrates across clients"
          Icon={PieChart}
          currencies={["USD", "TZS"]}
          metrics={[{ key: "revenue", label: "Revenue" }]}
          items={rev.clients.map((c) => ({
            key: c.client_id, label: c.name,
            meta: `${c.won} won · ${c.quoted} quoted · ${c.rfqs} RFQ${c.rfqs !== 1 ? "s" : ""}`,
            metricValues: { revenue: c.revenue },
          }))}
          onOpen={(id) => navigate(`/clients/${id}`)}
          emptyHint="No won revenue yet — win RFQs to see where revenue concentrates."
          testid="revenue-by-client"
        />
        <TrendPanel
          title="Quoted Value Trend"
          subtitleFor={(cur, m) => `Monthly ${m === "won" ? "won" : "quoted"} value by client (${cur})`}
          endpoint="/clients/trends"
          metrics={[{ key: "quoted", label: "Quoted" }, { key: "won", label: "Won (closed)" }]}
          defaultCurrency="USD"
          testid="client-trend"
        />
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search clients by name, contact or email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="clients-search" />
        </div>
        <SortSelect value={sort} onChange={setSort} testid="clients-sort" />
        <Select value={industryFilter} onValueChange={setIndustryFilter}>
          <SelectTrigger className="h-9 w-full text-sm sm:w-56" data-testid="clients-industry-filter"><SelectValue placeholder="All Industries" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Industries</SelectItem>
            {usedIndustries.map((v) => <SelectItem key={v} value={v} data-testid={`industry-option-${v}`}>{labelFor(v)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="hidden rounded-lg border border-border bg-white overflow-hidden sm:block">
        <table className="data-table w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
              <th className="w-10 px-5 py-3"><SelectAllCheckbox ids={paged.map((c) => c.id)} selected={sel.selected} onToggleAll={sel.toggleAll} /></th>
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Industry</th>
              <th className="px-5 py-3 font-medium">Primary Contact</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((c) => {
              const primary = (c.contacts || []).find((x) => x.primary) || (c.contacts || [])[0];
              return (
                <tr key={c.id} onClick={() => sel.selected.size > 0 ? sel.toggle(c.id) : navigate(`/clients/${c.id}`)} className={`cursor-pointer border-t border-border hover:bg-slate-50 ${sel.isSelected(c.id) ? "bg-[#EEF3FA]" : ""}`} data-testid={`client-row-${c.id}`}>
                  <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={sel.isSelected(c.id)} onCheckedChange={() => sel.toggle(c.id)} aria-label="Select client" data-testid={`bulk-select-client-${c.id}`} />
                  </td>
                  <td className="px-5 py-3 font-medium text-slate-900">{c.name}</td>
                  <td className="px-5 py-3">{labelFor(c.industry)}</td>
                  <td className="px-5 py-3 text-muted-foreground">{primary?.name || c.contact_person || "—"}</td>
                  <td className="px-5 py-3 text-muted-foreground">{primary?.email || c.contact_email || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={(e) => remove(e, c.id)} className="text-slate-500 hover:text-destructive" data-testid={`delete-client-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                      <ChevronRight className="h-4 w-4 text-slate-300" />
                    </div>
                  </td>
                </tr>
              );
            })}
            {!clients.length && <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">No clients yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="space-y-2.5 sm:hidden" data-testid="clients-cards">
        {paged.map((c) => {
          const primary = (c.contacts || []).find((x) => x.primary) || (c.contacts || [])[0];
          const contact = primary?.name || c.contact_person || "";
          const email = primary?.email || c.contact_email || "";
          return (
            <div key={c.id} role="button" tabIndex={0} onClick={() => sel.selected.size > 0 ? sel.toggle(c.id) : navigate(`/clients/${c.id}`)} className={`flex w-full items-center gap-3 rounded-lg border border-border p-3.5 text-left active:bg-slate-50 ${sel.isSelected(c.id) ? "bg-[#EEF3FA]" : "bg-white"}`} data-testid={`client-card-${c.id}`}>
              <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                <Checkbox checked={sel.isSelected(c.id)} onCheckedChange={() => sel.toggle(c.id)} aria-label="Select client" data-testid={`bulk-select-client-card-${c.id}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold text-slate-900">{c.name}</span>
                  <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">{labelFor(c.industry)}</span>
                </div>
                <div className="mt-1 truncate text-xs text-muted-foreground">{contact || "No contact"}{email ? ` · ${email}` : ""}</div>
              </div>
              <button onClick={(e) => remove(e, c.id)} className="shrink-0 p-1 text-slate-400 hover:text-destructive" data-testid={`delete-client-card-${c.id}`} aria-label="Delete client"><Trash2 className="h-4 w-4" /></button>
              <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
            </div>
          );
        })}
        {!filtered.length && <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No clients found.</div>}
      </div>

      <PaginationBar page={page} setPage={setPage} total={filtered.length} testid="clients-pagination" />

      <BulkBar count={sel.selected.size} onClear={sel.clear} onDelete={bulkDelete} canDelete={isOwner} deleting={bulkBusy}>
        <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-edit-button" className="w-full sm:w-auto">
          <Pencil className="mr-1.5 h-4 w-4" />Edit
        </Button>
      </BulkBar>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="client-bulk-edit-dialog">
          <DialogHeader><DialogTitle>Edit {sel.selected.size} client{sel.selected.size !== 1 ? "s" : ""}</DialogTitle>
          <DialogDescription>Apply an industry and/or visibility to all selected clients.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label>Industry <span className="font-normal text-muted-foreground">(leave blank to keep)</span></Label>
              <IndustrySelect value={bulkIndustry} onChange={setBulkIndustry} testid="bulk-industry-select" />
            </div>
            {isAdmin && (
            <div className="space-y-2">
              <Label>Visibility <span className="font-normal text-muted-foreground">(leave blank to keep)</span></Label>
              <VisibilityToggle value={bulkVisibility} onChange={setBulkVisibility} testid="bulk-visibility" />
            </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={bulkApply} disabled={bulkBusy} data-testid="bulk-apply-button">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="client-dialog">
          <DialogHeader><DialogTitle>New Client</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Folder Short Name <span className="font-normal text-muted-foreground">(Optional)</span></Label>
              <Input value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} placeholder="e.g. SWALA" data-testid="client-short-name-input" />
              <p className="text-xs text-muted-foreground">Used In Offline RFQ Folder Names Instead Of The Full Company Name.</p>
            </div>
            <div className="space-y-2">
              <Label>Industry</Label>
              <IndustrySelect value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} testid="client-industry-select" />
            </div>
            {isAdmin && (
            <div className="flex flex-col items-start gap-2">
              <Label>Visibility</Label>
              <VisibilityToggle value={form.visibility} onChange={(v) => setForm({ ...form, visibility: v })} testid="client-visibility" />
            </div>
            )}
            <ContactCardFields kind="client" f={form} set={(k, v) => setForm((p) => ({ ...p, [k]: v }))} />
            <p className="text-xs text-muted-foreground">You can add more contacts, billing details and notes on the next screen.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={!form.name} data-testid="client-save-button">Create & Open</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
